// A reader is signed into one account at a time. The library and the reading
// programme have separate entrances and separate passwords; moving between
// them means signing out and signing in again.
//
// Holding both at once was tried and withdrawn: localStorage is shared across
// tabs while the rendered page is not, so a tab could show one account and
// send its requests as the other.
//
// The session is also mirrored onto the flat keys the rest of the app already
// reads (Environment, PrivateRoute), so nothing else has to know about this.

export const LIBRARY = 'library';
export const PROGRAM = 'program';

const ACCOUNT_KEY = 'account';
const MIRROR_KEYS = ['authToken', 'uid', 'paymentAccountStripeUid'];

// Which account this tab is working as. Module scope, so it belongs to the tab
// and not to localStorage, which every tab of the origin shares.
//
// A tab that signs in or out has changed its own account on purpose and rebinds
// itself. A tab that finds the stored session no longer matching is looking at
// someone else's doing, and must not act on it.
let tabBoundTo = typeof localStorage === 'undefined' ? null : localStorage.getItem('uid');

const bindTab = () => {
  tabBoundTo = localStorage.getItem('uid');
};

export const sessionChangedUnderThisTab = () =>
  typeof localStorage !== 'undefined' && localStorage.getItem('uid') !== tabBoundTo;

export const currentAccount = () => {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNT_KEY)) || null;
  } catch (error) {
    return null;
  }
};

export const currentScope = () => {
  const account = currentAccount();

  return account && account.scope ? account.scope : LIBRARY;
};

export const inProgramme = () => {
  const account = currentAccount();

  return Boolean(account && account.programName);
};

export const signInPathFor = scope => (scope === PROGRAM ? '/program/sign-in' : '/sign-in');

export const signIn = (scope, account) => {
  const stored = { ...account, scope };

  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(stored));
  localStorage.setItem('authToken', stored.token || '');
  localStorage.setItem('uid', stored.uid || '');
  localStorage.setItem('paymentAccountStripeUid', stored.paymentAccountStripeUid || '');
  bindTab();

  return stored;
};

// Ends the session. Reaches this browser only: a token on another device stays
// valid until it expires.
export const signOut = () => {
  localStorage.removeItem(ACCOUNT_KEY);
  MIRROR_KEYS.forEach(key => localStorage.removeItem(key));
  bindTab();
};

// Builds what is kept from what sessionCreate returned.
export const accountFromSession = user => ({
  uid: user.dbId,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  paymentAccountStripeUid: user.paymentAccountStripeUid,
  programName: user.program ? user.program.name : null,
  bookAllowance: user.bookAllowance
});
